import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { roleApi } from '../../../services/api';
import { Role } from '../../../types';
import PermissionModuleSelector from '../../../components/PermissionModuleSelector';
import { showSuccess, showError } from '../../../utils/toast';

interface RoleFormProps {
  role?: Role;
  onClose: () => void;
  onSuccess: () => void;
}

const RoleForm: React.FC<RoleFormProps> = ({ role, onClose, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: role?.name || '',
    permissions: role?.permissions?.map((p) => p.id) || [] as number[],
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({
      ...prev,
      name: e.target.value,
    }));
  };

  const handlePermissionsChange = (permissionIds: number[]) => {
    setForm((prev) => ({
      ...prev,
      permissions: permissionIds,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.name.trim()) {
      setError('Role name is required');
      return;
    }

    if (form.permissions.length === 0) {
      setError('At least one permission must be selected');
      return;
    }

    setSubmitting(true);
    try {
      if (role) {
        await roleApi.update(role.id, {
          name: form.name,
          permissions: form.permissions,
        });
        showSuccess('Role updated successfully');
      } else {
        await roleApi.create({
          name: form.name,
          permissions: form.permissions,
        });
        showSuccess('Role created successfully');
      }
      onSuccess();
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to save role';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] pt-20 pb-8" onClick={onClose}>
      <div
        className="theme-bg-primary rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[calc(100vh-8rem)] overflow-y-auto theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 theme-bg-primary border-b theme-border px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-lg font-semibold theme-text-primary">
            {role ? 'Edit Role' : 'Create Role'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:opacity-80 rounded-full transition-opacity"
          >
            <X className="w-4 h-4 theme-text-secondary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">
              Role Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={handleChange}
              required
              className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter role name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-1">
              Permissions <span className="text-red-500">*</span>
            </label>
            <p className="text-sm theme-text-secondary mb-2">
              Select permissions for this role. Permissions are organized by module.
            </p>
            <PermissionModuleSelector
              selectedPermissions={form.permissions}
              onChange={handlePermissionsChange}
            />
            {form.permissions.length > 0 && (
              <p className="mt-1.5 text-sm theme-text-secondary">
                {form.permissions.length} permission(s) selected
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm theme-border rounded theme-bg-secondary theme-text-primary hover:opacity-80"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || form.permissions.length === 0}
              className="px-3 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded hover:shadow-lg transition-shadow disabled:opacity-50"
            >
              {submitting ? 'Saving...' : role ? 'Update Role' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default RoleForm;

